<template>
    <BaseForm @submit.prevent>
        <template #fields>
            <Toggle
                :value="values.singleEvent"
                :label="$t('limit-to-single-event')"
                :help-button-text="$t('limit-to-single-event-help')"
                @change="setFieldValue('singleEvent', $event)"
                @blur="setFieldTouched('singleEvent', true)"
            ></Toggle>
            <FieldGroup :title="$t('identifiers')">
                <TextField
                    :value="values.nameShort"
                    :label="$t('name-short')"
                    :error-messages="errors.nameShort"
                    :required="true"
                    @input="setFieldValue('nameShort', ($event.target as HTMLInputElement).value)"
                    @blur="setFieldTouched('nameShort', true)"
                />
                <TextField
                    :value="values.nameLong"
                    :label="$t('name-long')"
                    :error-messages="errors.nameLong"
                    :required="true"
                    @input="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                    @blur="setFieldTouched('nameLong', true)"
                />
                <TextAreaField
                    :value="values.subtitle"
                    :label="$t('subtitle')"
                    :error-messages="errors.subtitle"
                    :max-length="subtitleMaxLength"
                    @input="setFieldValue('subtitle', ($event.target as HTMLInputElement).value)"
                    @blur="setFieldTouched('subtitle', true)"
                />
            </FieldGroup>
        </template>
        <template #buttons>
            <Button v-if="isTouched" size="Base" variant="Danger-Light" @click="onDiscard">
                <template #icon>
                    <UndoIcon class="size-5" />
                </template>
                {{ $t('discard') }}
            </Button>
            <Button v-else size="Base" variant="Neutral-Light" @click="$router.back">
                <template #icon>
                    <CloseIcon class="size-5" />
                </template>
                {{ $t('abort') }}
            </Button>
            <Button
                size="Base"
                variant="Info-Strong"
                :disabled-explanation="saveButtonDisabledExplanation"
                type="submit"
                @click="onSave"
            >
                <template #icon>
                    <AllDoneIcon />
                </template>
                {{ $t('create') }}
            </Button>
        </template>
    </BaseForm>
</template>

<script setup lang="ts">
import Button from '@/components/Button/Button.vue';
import BaseForm from '@/components/Form/BaseForm.vue';
import FieldGroup from '@/components/Form/FieldGroup.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import TextField from '@/components/Form/TextField.vue';
import Toggle from '@/components/Form/Toggle.vue';
import AllDoneIcon from '@/components/Icon/AllDoneIcon.vue';
import CloseIcon from '@/components/Icon/CloseIcon.vue';
import UndoIcon from '@/components/Icon/UndoIcon.vue';
import { useForm } from '@/forms/composables/useForm';
import { computed } from 'vue';
import { useEventSeriesCreate } from '../composables/useEventSeriesCreate';
import { goToEventSeries } from '../event';
import { EventSeriesCreateSchema } from './schemas';

const {
    values,
    errors,
    setFieldTouched,
    setFieldValue,
    validate,
    saveButtonDisabledExplanation,
    isTouched,
    onDiscard,
} = useForm(
    EventSeriesCreateSchema,
    computed(() => ({
        singleEvent: false,
        nameShort: '',
        nameLong: '',
        subtitle: '',
    })),
);

const subtitleMaxLength = EventSeriesCreateSchema.shape.subtitle.maxLength ?? undefined;

const { create } = useEventSeriesCreate(values);

async function onSave() {
    const valid = await validate();
    if (valid) {
        const { id } = await create();
        if (id) {
            goToEventSeries(id);
        }
    }
}
</script>
